import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users as UsersIcon, HardHat, Search, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Crew() {
  const { token } = useAuth();
  const [crewMembers, setCrewMembers] = useState([]);
  const [crafts, setCrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [deletingCrew, setDeletingCrew] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [nameFilter, setNameFilter] = useState('');
  const [craftFilter, setCraftFilter] = useState('all');
  // Sorting
  const [sortColumn, setSortColumn] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');

  const [formData, setFormData] = useState({
    fullName: '',
    employeeId: '',
    eligibleCraftIds: [],
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [crewRes, craftsRes] = await Promise.all([
        axios.get(`${API}/crew-members`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/crafts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCrewMembers(crewRes.data);
      setCrafts(craftsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load crew data');
    } finally {
      setLoading(false);
    }
  };

  const getCraftName = (craftId) => {
    const craft = crafts.find(c => c.id === craftId);
    return craft ? craft.name : craftId;
  };

  const handleOpenDialog = (crew = null) => {
    if (crew) {
      setEditingCrew(crew);
      setFormData({
        fullName: crew.fullName,
        employeeId: crew.employeeId || '',
        eligibleCraftIds: crew.eligibleCraftIds || [],
        active: crew.active
      });
    } else {
      setEditingCrew(null);
      setFormData({
        fullName: '',
        employeeId: '',
        eligibleCraftIds: [],
        active: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingCrew) {
        await axios.patch(`${API}/crew-members/${editingCrew.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Crew member updated successfully');
      } else {
        await axios.post(`${API}/crew-members`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Crew member added successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save crew member:', error);
      toast.error(error.response?.data?.detail || 'Failed to save crew member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCrew) return;

    try {
      await axios.delete(`${API}/crew-members/${deletingCrew.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Crew member deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingCrew(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete crew member:', error);
      toast.error('Failed to delete crew member');
    }
  };

  const toggleCraft = (craftId) => {
    setFormData(prev => ({
      ...prev,
      eligibleCraftIds: prev.eligibleCraftIds.includes(craftId)
        ? prev.eligibleCraftIds.filter(id => id !== craftId)
        : [...prev.eligibleCraftIds, craftId]
    }));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-slate-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 ml-1 text-[#1A3668]" />
      : <ChevronDown className="w-3.5 h-3.5 ml-1 text-[#1A3668]" />;
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...crewMembers];

    // Filter by name
    if (nameFilter.trim()) {
      const q = nameFilter.toLowerCase();
      result = result.filter(c => c.fullName.toLowerCase().includes(q));
    }

    // Filter by craft
    if (craftFilter && craftFilter !== 'all') {
      result = result.filter(c => c.eligibleCraftIds?.includes(craftFilter));
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'fullName':
          aVal = a.fullName.toLowerCase();
          bVal = b.fullName.toLowerCase();
          break;
        case 'employeeId':
          aVal = (a.employeeId || '').toLowerCase();
          bVal = (b.employeeId || '').toLowerCase();
          break;
        case 'crafts':
          aVal = (a.eligibleCraftIds || []).map(id => getCraftName(id)).join(', ').toLowerCase();
          bVal = (b.eligibleCraftIds || []).map(id => getCraftName(id)).join(', ').toLowerCase();
          break;
        case 'active':
          aVal = a.active ? 1 : 0;
          bVal = b.active ? 1 : 0;
          break;
        default:
          aVal = '';
          bVal = '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [crewMembers, nameFilter, craftFilter, sortColumn, sortDir, crafts]);

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading crew...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="crew-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900">Crew Management</h1>
          <p className="mt-2 text-lg text-slate-700">Manage your workforce and their skills</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          data-testid="create-crew-button"
          className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Crew Member
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="crew-name-filter"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Search by name..."
            className="pl-9 rounded-sm border-2"
          />
        </div>
        <Select value={craftFilter} onValueChange={setCraftFilter}>
          <SelectTrigger data-testid="crew-craft-filter" className="w-[200px] rounded-sm border-2">
            <SelectValue placeholder="All Crafts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Crafts</SelectItem>
            {crafts.map((craft) => (
              <SelectItem key={craft.id} value={craft.id}>{craft.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="self-center text-sm text-slate-500" data-testid="crew-count">
          {filteredAndSorted.length} of {crewMembers.length} members
        </span>
      </div>

      {crewMembers.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <UsersIcon className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No crew members yet</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="outline"
              className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
            >
              Add your first crew member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredAndSorted.map((crew) => (
              <Card
                key={crew.id}
                data-testid={`crew-card-${crew.id}`}
                className={`border-slate-200 shadow-sm rounded-sm ${!crew.active ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-slate-900">{crew.fullName}</span>
                        {!crew.active && <Badge variant="outline" className="text-xs border-slate-300 text-slate-500">Inactive</Badge>}
                      </div>
                      {crew.employeeId && <p className="text-sm text-slate-500 font-mono">{crew.employeeId}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button onClick={() => handleOpenDialog(crew)} data-testid={`edit-crew-${crew.id}`}
                        variant="outline" size="sm" className="border-slate-200 hover:border-[#1A3668] rounded-sm h-8 w-8 p-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button onClick={() => { setDeletingCrew(crew); setDeleteDialogOpen(true); }}
                        data-testid={`delete-crew-${crew.id}`} variant="outline" size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50 rounded-sm h-8 w-8 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {crew.eligibleCraftIds?.length > 0 ? crew.eligibleCraftIds.map(craftId => (
                      <Badge key={craftId} className="bg-[#1A3668] text-white rounded-sm text-xs">{getCraftName(craftId)}</Badge>
                    )) : <span className="text-sm text-slate-400">No crafts</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full" data-testid="crew-table">
              <thead>
                <tr className="bg-slate-50 border-b-2 border-slate-200">
                  <th className="text-left p-4 cursor-pointer select-none" onClick={() => handleSort('fullName')}>
                    <span className="flex items-center text-sm font-bold uppercase tracking-wider text-slate-700">
                      Name <SortIcon column="fullName" />
                    </span>
                  </th>
                  <th className="text-left p-4 cursor-pointer select-none" onClick={() => handleSort('employeeId')}>
                    <span className="flex items-center text-sm font-bold uppercase tracking-wider text-slate-700">
                      Employee ID <SortIcon column="employeeId" />
                    </span>
                  </th>
                  <th className="text-left p-4 cursor-pointer select-none" onClick={() => handleSort('crafts')}>
                    <span className="flex items-center text-sm font-bold uppercase tracking-wider text-slate-700">
                      Crafts <SortIcon column="crafts" />
                    </span>
                  </th>
                  <th className="text-center p-4 cursor-pointer select-none" onClick={() => handleSort('active')}>
                    <span className="flex items-center justify-center text-sm font-bold uppercase tracking-wider text-slate-700">
                      Status <SortIcon column="active" />
                    </span>
                  </th>
                  <th className="text-center p-4 text-sm font-bold uppercase tracking-wider text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500">
                      No crew members match your filters
                    </td>
                  </tr>
                ) : (
                  filteredAndSorted.map((crew) => (
                    <tr
                      key={crew.id}
                      data-testid={`crew-row-${crew.id}`}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${!crew.active ? 'opacity-60' : ''}`}
                    >
                      <td className="p-4">
                        <span className="font-bold text-slate-900">{crew.fullName}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-500 font-mono">{crew.employeeId || '-'}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {crew.eligibleCraftIds?.length > 0 ? crew.eligibleCraftIds.map(craftId => (
                            <Badge key={craftId} className="bg-[#1A3668] text-white rounded-sm text-xs">{getCraftName(craftId)}</Badge>
                          )) : <span className="text-sm text-slate-400">None</span>}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {crew.active ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200 rounded-sm">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 border-slate-300 rounded-sm">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 justify-center">
                          <Button
                            onClick={() => handleOpenDialog(crew)}
                            data-testid={`edit-crew-${crew.id}`}
                            variant="outline"
                            size="sm"
                            className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            onClick={() => { setDeletingCrew(crew); setDeleteDialogOpen(true); }}
                            data-testid={`delete-crew-${crew.id}`}
                            variant="outline"
                            size="sm"
                            className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCrew ? 'Edit Crew Member' : 'Add Crew Member'}
            </DialogTitle>
            <DialogDescription>
              {editingCrew ? 'Update crew member information and skills' : 'Add a new member to your workforce'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="fullName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Full Name *
                </Label>
                <Input
                  id="fullName"
                  data-testid="crew-name-input"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="John Smith"
                  required
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              
              <div>
                <Label htmlFor="employeeId" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Employee ID (Optional)
                </Label>
                <Input
                  id="employeeId"
                  data-testid="crew-employee-id-input"
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  placeholder="EMP-001"
                  className="mt-2 rounded-sm border-2"
                />
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                  Eligible Crafts *
                </Label>
                <div className="space-y-2 border-2 border-slate-200 rounded-sm p-4 max-h-48 overflow-y-auto">
                  {crafts.length === 0 ? (
                    <p className="text-sm text-slate-500">No crafts available. Add crafts first in Rates page.</p>
                  ) : (
                    crafts.map((craft) => (
                      <label
                        key={craft.id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.eligibleCraftIds.includes(craft.id)}
                          onChange={() => toggleCraft(craft.id)}
                          className="w-4 h-4 text-[#1A3668] border-2 border-slate-300 rounded focus:ring-[#1A3668]"
                        />
                        <span className="text-sm font-medium text-slate-700">{craft.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-sm">
                <div>
                  <Label htmlFor="active" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Active Status
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">Is this crew member currently active?</p>
                </div>
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-2 border-slate-200 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-crew-button"
                disabled={submitting}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {submitting ? 'Saving...' : editingCrew ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Crew Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingCrew?.fullName}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-crew"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
